
import React, { useEffect, useRef } from 'react';
import { AspectRatio } from "@/components/ui/aspect-ratio";

const processSteps = [
  {
    number: 1,
    title: "Explain your requirement",
    description: "Define your role and key criteria you have.",
  },
  {
    number: 2,
    title: "Sourcing Assistant starts working",
    description: "It finds, contacts, reviews and even interviews candidates to find the best fitting candidates.",
  },
  {
    number: 3,
    title: "Start getting top candidates",
    description: "Get candidates in your ATS, in a Google sheet or in Cutshort. Close the ones that fit the best.",
  },
];

const SimpleProcess = () => {
  const sectionRef = useRef<HTMLDivElement>(null);
  const videoSectionRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          entry.target.classList.add('opacity-100');
          observer.unobserve(entry.target);
        }
      },
      { threshold: 0.1 }
    );

    const videoObserver = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          entry.target.classList.add('opacity-100');
          videoObserver.unobserve(entry.target);
        }
      },
      { threshold: 0.1 }
    );

    if (sectionRef.current) {
      observer.observe(sectionRef.current);
    }

    if (videoSectionRef.current) {
      videoObserver.observe(videoSectionRef.current);
    }

    return () => {
      if (sectionRef.current) {
        observer.unobserve(sectionRef.current);
      }
      if (videoSectionRef.current) {
        videoObserver.unobserve(videoSectionRef.current);
      }
    };
  }, []);

  return (
    <section 
      ref={sectionRef}
      className="py-20 bg-brand-offwhite opacity-0 transition-opacity duration-700"
    >
      <div className="container mx-auto px-4 md:px-6">
        <div className="text-center mb-12">
          <h2 className="text-3xl md:text-4xl font-bold text-brand-darkgrey mb-4">
            It can't get any simpler
          </h2>
          <p className="text-xl text-brand-grey max-w-2xl mx-auto">
            No need to buy tools. No need to hire more recruiters.
          </p>
        </div>

        <div className="grid md:grid-cols-3 gap-8 max-w-5xl mx-auto">
          {processSteps.map((step, index) => (
            <div 
              key={step.number}
              className={`glass-panel p-6 hover-scale ${
                index === 1 ? 'md:mt-8' : index === 2 ? 'md:mt-16' : ''
              }`}
            >
              <div className="bg-brand-blue/10 w-12 h-12 flex items-center justify-center rounded-full mb-4">
                <div className="text-brand-blue font-bold text-xl">{step.number}</div>
              </div>
              <h3 className="text-xl font-semibold text-brand-darkgrey mb-3">
                {step.title}
              </h3>
              <p className="text-brand-grey">{step.description}</p>
            </div>
          ))}
        </div>

        <div 
          ref={videoSectionRef}
          className="mt-16 opacity-0 transition-opacity duration-1000 max-w-4xl mx-auto"
        >
          <div className="text-center mb-8">
            <h3 className="text-2xl md:text-3xl font-bold text-brand-darkgrey mb-4">
              See Sourcing Assistant in Action
            </h3>
            <p className="text-lg text-brand-grey">
              No tools to learn and buy. Just submit your job details
            </p>
          </div>
          
          <div className="relative rounded-2xl overflow-hidden shadow-xl">
            <div className="glass-card p-1 rounded-2xl">
              <div className="absolute -top-6 -right-6 h-16 w-16 rounded-full bg-brand-yellow/20 blur-xl animate-pulse-light" />
              <div className="absolute -bottom-6 -left-6 h-16 w-16 rounded-full bg-brand-blue/20 blur-xl animate-pulse-light" />
              
              <div className="aspect-video w-full">
                <iframe
                  className="rounded-xl"
                  width="100%"
                  height="100%"
                  src="https://www.youtube.com/embed/D4poPbkSqaI?si=FFU2FvTYfBo-jMjX"
                  title="YouTube video player"
                  frameBorder="0"
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                  referrerPolicy="strict-origin-when-cross-origin"
                  allowFullScreen
                />
              </div>
            </div>
            <div className="absolute inset-0 rounded-2xl border-2 border-white/20 pointer-events-none" />
            <div className="absolute top-4 left-4 h-2 w-2 rounded-full bg-brand-yellow animate-pulse-light" />
            <div className="absolute bottom-4 right-4 h-2 w-2 rounded-full bg-brand-blue animate-pulse-light" />
          </div>
        </div>

        <div className="mt-16 text-center">
          <a
            href="https://cutshort.io/a/generic-emp-form"
            target="_blank"
            rel="noopener noreferrer"
            className="bg-brand-blue hover:bg-brand-blue/90 text-white px-8 py-4 rounded-xl text-lg font-medium transition-all shadow-md hover:shadow-xl hover:translate-y-[-2px] inline-block"
          >
            Start getting candidates
          </a>
        </div>
      </div>
    </section>
  );
};

export default SimpleProcess;
